// Schema markup generators for different content types

export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Lumi Bears",
  "description": "Premium comfort companions designed to bring peace and emotional support to children and families through innovative wellness technology.",
  "url": "https://lumibears.com",
  "logo": {
    "@type": "ImageObject",
    "url": "https://lumibears.com/logo.png",
    "width": 200,
    "height": 200
  },
  "contactPoint": [{
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "support@lumibears.com",
    "areaServed": "US",
    "availableLanguage": "English"
  }],
  "sameAs": [],
  "foundingDate": "2024",
  "numberOfEmployees": "2-10",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US"
  }
});

export const generateProductSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": product.name,
  "description": product.description,
  "brand": {
    "@type": "Brand",
    "name": "Lumi Bears"
  },
  "manufacturer": {
    "@type": "Organization",
    "name": "Lumi Bears"
  },
  "image": product.images || [`https://lumibears.com${product.image}`],
  "sku": product.id,
  "offers": {
    "@type": "Offer",
    "price": product.price,
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "seller": {
      "@type": "Organization",
      "name": "Lumi Bears"
    },
    "url": `https://lumibears.com/products/${product.id}`
  },
  "aggregateRating": product.rating ? {
    "@type": "AggregateRating",
    "ratingValue": product.rating.average,
    "reviewCount": product.rating.count,
    "bestRating": 5,
    "worstRating": 1
  } : undefined,
  "category": "Toys & Games > Stuffed Animals",
  "audience": {
    "@type": "PeopleAudience",
    "suggestedMinAge": 3,
    "suggestedMaxAge": 12
  }
});

export const generateArticleSchema = (blogPost) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": blogPost.title,
  "description": blogPost.excerpt,
  "image": [`https://lumibears.com${blogPost.image}`],
  "datePublished": blogPost.publishedDate,
  "dateModified": blogPost.publishedDate,
  "author": {
    "@type": "Organization",
    "name": "Lumi Team",
    "url": "https://lumibears.com"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Lumi Bears",
    "logo": {
      "@type": "ImageObject",
      "url": "https://lumibears.com/logo.png",
      "width": 200,
      "height": 200
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://lumibears.com/blog/${blogPost.slug}`
  },
  "keywords": blogPost.tags?.join(', '),
  "articleSection": blogPost.category,
  "wordCount": Math.floor(blogPost.content.length / 5), // Approximate word count
  "inLanguage": "en-US"
});

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateBreadcrumbSchema = (breadcrumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": `https://lumibears.com${crumb.url}`
  }))
});

export const generateReviewSchema = (reviews, productName) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": productName,
  "review": reviews.map(review => ({
    "@type": "Review",
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": review.rating,
      "bestRating": 5
    },
    "author": {
      "@type": "Person",
      "name": review.author
    },
    "reviewBody": review.text,
    "datePublished": review.date
  })),
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length,
    "reviewCount": reviews.length
  }
});

export const generateLocalBusinessSchema = (location) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": `Lumi Bears ${location.city}`,
  "description": `Premium wellness companions for children and families in ${location.city}. Emotional support stuffed animals with innovative technology.`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": location.city,
    "addressRegion": location.state,
    "addressCountry": "US"
  },
  "telephone": location.phone || "+1-555-LUMI-BEAR",
  "url": `https://lumibears.com/locations/${location.slug}`,
  "priceRange": "$$",
  "category": "Toy Store",
  "brand": "Lumi Bears",
  "parentOrganization": {
    "@type": "Organization",
    "name": "Lumi Bears"
  }
});

export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Lumi Bears",
  "url": "https://lumibears.com",
  "description": "Premium comfort companions designed to bring peace and emotional support to children and families.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://lumibears.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Lumi Bears"
  }
});